import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'

interface FormSectionProps {
  title: string
  description?: string
  icon?: ReactNode
  children: ReactNode
  columns?: 1 | 2 | 3
  className?: string
  aside?: ReactNode
}

/** A titled group of fields laid out on a responsive grid. */
export function FormSection({
  title,
  description,
  icon,
  children,
  columns = 2,
  className,
  aside,
}: FormSectionProps) {
  return (
    <section
      className={cn('rounded-card border border-line bg-surface shadow-card', className)}
      aria-labelledby={`${title}-heading`}
    >
      <div className="flex items-start gap-3 border-b border-line px-5 py-4">
        {icon ? (
          <span className="grid size-9 shrink-0 place-items-center rounded-control bg-brand-50 text-brand-600 [&_svg]:size-[1.125rem]">
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h2 id={`${title}-heading`} className="text-[0.9375rem] font-semibold">
            {title}
          </h2>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {aside}
      </div>
      <div
        className={cn(
          'grid gap-x-5 gap-y-4 p-5',
          columns === 2 && 'sm:grid-cols-2',
          columns === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {children}
      </div>
    </section>
  )
}

/** Sticky action bar for long forms. */
export function FormActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 -mx-4 mt-6 flex flex-col-reverse gap-2 border-t border-line bg-surface/90 px-4 py-3 backdrop-blur-md sm:mx-0 sm:flex-row sm:justify-end sm:rounded-card sm:border sm:shadow-raised',
        className,
      )}
    >
      {children}
    </div>
  )
}
